import { Box } from '@mui/material';
import { useAutoLoadCompanyById } from '@pn/core/loaders/company/getCompanyById';
import { LoadingMessage } from '@pn/ui/loading/LoadingMessage';
import React from 'react';
import { Route, Routes } from 'react-router-dom';
import { ToolsPanel } from 'src/ui/components/tools/ToolsPanel';
import { AppHeader } from './components/AppHeader';
import { useBOEIntel } from './contexts/useBOEIntel';
import { useListenForWorkspaceItems } from './hooks/useListenForWorkspaceItems';
import { NavDrawerContent } from './navigation/NavDrawerContent';

const Map = React.lazy(() => import('./map/ActivityMap'));
const Reports = React.lazy(() => import('./report/Reports'));
const LoadedReport = React.lazy(() => import('./report/loaded-report'));
const Company = React.lazy(() => import('src/ui/company/Company'));
const Companies = React.lazy(() => import('src/ui/company/Companies'));
const Charts = React.lazy(() => import('src/ui/company-charts/Charts'));
const MergersAndAcquisitions = React.lazy(
  () => import('./mergers-and-acquisitions/MergersAndAcquisitions')
);
const CompetitionBureau = React.lazy(
  () => import('./competition-bureau/CompetitionBureau')
);
const BusinessAssociateOwnerLookup = React.lazy(
  () => import('src/ui/business-associates/BusinessAssociateOwnerLookup')
);
const ListReport = React.lazy(() => import('./list-report'));
const DataUpdates = React.lazy(() => import('./pages/data/DataUpdates'));

const Plays = React.lazy(() => import('./plays/Plays'));
const Play = React.lazy(() => import('./plays/Play'));

export function Dashboards() {
  const { isDataInitialized } = useBOEIntel();

  useListenForWorkspaceItems();
  useAutoLoadCompanyById();

  return (
    <Box
      sx={{
        display: 'grid',
        height: '100vh',
        gridTemplateColumns: 'min-content 1fr',
        gridTemplateRows: 'min-content 1fr',
        gridTemplateAreas: `
          "header header"
          "menu content"
        `,
        '@media print': {
          gridTemplateColumns: '1fr',
          gridTemplateRows: '1fr',
          gridTemplateAreas: '"content"',
        },
      }}
    >
      <AppHeader />
      <Box
        sx={(theme) => ({
          gridArea: 'menu',
          borderRight: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.background.paper,
          [theme.breakpoints.down('lg')]: {
            display: 'none',
          },
          overflowY: 'auto',
          overflowX: 'hidden',
          '@media print': { display: 'none' },
        })}
      >
        <NavDrawerContent />
      </Box>
      <Box
        sx={(theme) => ({
          gridArea: 'content',
          overflowY: 'auto',
          position: 'relative',
          backgroundColor: theme.palette.background.secondary,
        })}
        id="pageContainerID"
      >
        {!isDataInitialized ? (
          <Loading />
        ) : (
          <>
            <React.Suspense fallback={<Loading />}>
              <Routes>
                <Route
                  path="list_report/:workspaceItemId/*"
                  element={<ListReport />}
                />
                <Route
                  path="ba-lookup"
                  element={<BusinessAssociateOwnerLookup />}
                />
                <Route
                  path="m-and-a-database"
                  element={<MergersAndAcquisitions />}
                />
                <Route
                  path="competition-bureau"
                  element={<CompetitionBureau />}
                />
                <Route path="charts" element={<Charts />} />
                <Route path="map/:dataType/:id" element={<Map />} />
                <Route path="map/:default_tab" element={<Map />} />
                <Route path="map" element={<Map />} />
                <Route path="reports/:reportId/*" element={<LoadedReport />} />
                <Route path="reports" element={<Reports />} />
                <Route path="plays/:playId" element={<Play />} />
                <Route path="plays" element={<Plays />} />
                <Route path="companies/:companyId/*" element={<Company />} />
                <Route path="companies" element={<Companies />} />
                <Route path="data-updates" element={<DataUpdates />} />
              </Routes>
            </React.Suspense>
            <ToolsPanel />
          </>
        )}
      </Box>
    </Box>
  );
}

function Loading() {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
      }}
    >
      <LoadingMessage />
    </Box>
  );
}
